// ForgotPassword.jsx - עם EmailVerification כמו Registry
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Login.module.css'; // Using the same CSS as Login
import { Mail, Lock, ArrowRight, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { forgotPassword, verifyResetCode, resetPassword, resendVerificationCode } from '../../Fetch/fetchUser';
import EmailVerification from './EmailVerification'; // Import the verification component

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [step, setStep] = useState('email'); // 'email', 'code', 'password'
    const [showVerificationHelp, setShowVerificationHelp] = useState(false);

    // Email verification states - like Registry.jsx
    const [verificationStep, setVerificationStep] = useState('form'); // 'form', 'verification'
    const [pendingEmail, setPendingEmail] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    // Get email from navigation state if it exists
    useEffect(() => {
        if (location.state && location.state.email) {
            setEmail(location.state.email);
        }
        
        // Handle success message from email verification
        if (location.state && location.state.message) {
            setMessage(location.state.message);
            setShowVerificationHelp(false);
        }
    }, [location.state]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Clear messages when step changes
    useEffect(() => {
        if (verificationStep === 'form') {
            setMessage('');
            setError('');
            setShowVerificationHelp(false);
        }
    }, [step, verificationStep]);

    // Step 1: Send reset code to email
    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setShowVerificationHelp(false);

        if (!email.trim()) {
            setError('נדרש להזין כתובת מייל');
            return;
        }

        if (!validateEmail(email)) {
            setError('נא להזין כתובת מייל תקינה');
            return;
        }

        setLoading(true);

        try {
            const response = await forgotPassword(email.trim());
            
            setMessage('קוד איפוס נשלח לכתובת המייל שלך');
            setStep('code');
        } catch (error) {
            
            const errorMessage = error.message || 'שגיאה בשליחת קוד האיפוס';
            
            // Check for email verification errors
            if (errorMessage.includes('אנא אמת את כתובת המייל') || 
                errorMessage.includes('אמת את כתובת המייל') || 
                errorMessage.includes('לפני איפוס הסיסמה') ||
                errorMessage.includes('verify') || 
                errorMessage.includes('verification')) {
                setError('כתובת המייל טרם אומתה. אנא אמת את כתובת המייל שלך קודם על ידי לחיצה על הקישור שנשלח אליך במייל הרישום.');
                setShowVerificationHelp(true);
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Verify reset code
    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');

        if (!resetCode.trim()) {
            setError('נדרש להזין קוד אימות');
            return;
        }

        if (resetCode.trim().length !== 6) {
            setError('קוד האימות חייב להכיל 6 ספרות');
            return;
        }

        setLoading(true);

        try {
            const response = await verifyResetCode(email.trim(), resetCode.trim());
            
            setMessage('קוד האימות תקין');
            setStep('password');
        } catch (error) {
            setError(error.message || 'קוד אימות שגוי. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Reset password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');

        if (!newPassword.trim()) {
            setError('נדרש להזין סיסמה חדשה');
            return;
        }

        if (newPassword.length < 6) {
            setError('הסיסמה חייבת להכיל לפחות 6 תווים');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('הסיסמאות אינן תואמות');
            return;
        }

        setLoading(true);

        try {
            const response = await resetPassword(email.trim(), resetCode.trim(), newPassword);
            
            setMessage('הסיסמה שונתה בהצלחה!');
            
            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('/login', { 
                    state: { 
                        message: 'הסיסמה שונתה בהצלחה! אנא התחבר עם הסיסמה החדשה.' 
                    } 
                });
            }, 2000);
        } catch (error) {
            setError(error.message || 'שגיאה בשינוי הסיסמה. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    // Resend verification email - UPDATED LIKE REGISTRY
    const handleResendVerification = async () => {
        setMessage('');
        setError('');
        setLoading(true);

        try {
            
            // Instead of using resendVerificationCode, we'll call a specific endpoint
            // for email verification from forgot password
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7204'}/api/auth/resend-email-verification-for-reset`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email.trim() }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'שגיאה בשליחת קוד האימות');
            }
            
            // Store pending verification info and move to verification step
            setPendingEmail(email.trim());
            setVerificationStep('verification');
        } catch (error) {
            setError(error.message || 'שגיאה בשליחת קוד האימות. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    // Handle email verification completion - like Registry.jsx
    const handleVerificationComplete = (result) => {
        // Navigate to login page with success message
        navigate('/login', {
            state: {
                message: 'כתובת המייל אומתה בהצלחה! כעת תוכל לאפס את הסיסמה. אנא התחבר למערכת.'
            }
        });
    };

    // Handle back from email verification - like Registry.jsx
    const handleBackToForm = () => {
        setVerificationStep('form');
        setPendingEmail('');
    };

    // Resend reset code
    const handleResendCode = async () => {
        setMessage('');
        setError('');
        setLoading(true);

        try {
            const response = await forgotPassword(email.trim());
            setMessage('קוד איפוס חדש נשלח לכתובת המייל שלך');
        } catch (error) {
            setError(error.message || 'שגיאה בשליחת קוד חדש. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    // Go back to previous step
    const handleGoBack = () => {
        setMessage('');
        setError('');
        setShowVerificationHelp(false);
        
        if (step === 'code') {
            setStep('email');
            setResetCode('');
        } else if (step === 'password') {
            setStep('code');
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    const getStepTitle = () => {
        switch (step) {
            case 'email': return 'איפוס סיסמה';
            case 'code': return 'הזן קוד אימות';
            case 'password': return 'סיסמה חדשה';
            default: return 'איפוס סיסמה';
        }
    };

    const getStepSubtitle = () => {
        switch (step) {
            case 'email': return 'הזן את כתובת המייל שלך לקבלת קוד איפוס';
            case 'code': return 'הזן את קוד האימות שנשלח אליך במייל';
            case 'password': return 'הזן את הסיסמה החדשה שלך';
            default: return '';
        }
    };

    // Show verification component if in verification step - like Registry.jsx
    if (verificationStep === 'verification') {        
        return (
            <EmailVerification
                email={pendingEmail}
                onVerificationComplete={handleVerificationComplete}
                onBack={handleBackToForm}
                fromForgotPassword={true}  // צריך להעביר את הפרמטר הזה
            />
        );
    }

    // Main forgot password form - like Registry.jsx structure
    return (
        <div className={styles.loginContainer} dir="rtl">
            <div className={styles.loginCardWrapper}>
                <div className={styles.loginCard}>
                    {/* Progress indicator */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        marginBottom: '1rem',
                        gap: '0.5rem'
                    }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: step === 'email' ? '#007bff' : '#e9ecef'
                        }} />
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: step === 'code' ? '#007bff' : '#e9ecef'
                        }} />
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: step === 'password' ? '#007bff' : '#e9ecef'
                        }} />
                    </div>

                    {error && (
                        <div className={`${styles.messageContainer} ${styles.error}`}>
                            <AlertCircle size={20} />
                            <p className={styles.message}>{error}</p>
                        </div>
                    )}
                    
                    {message && (
                        <div className={`${styles.messageContainer} ${styles.success}`}>
                            <CheckCircle size={20} />
                            <p className={styles.message}>{message}</p>
                        </div>
                    )}

                    <div className={styles.formContainer}>
                        <div className={styles.header}>
                            <div className={styles.logoContainer}>
                                <Mail className={styles.logoIcon} />
                            </div>
                            <h1 className={styles.appTitle}>{getStepTitle()}</h1>
                            <p className={styles.appSubtitle}>{getStepSubtitle()}</p>
                        </div>

                        {step === 'email' && (
                            <form onSubmit={handleForgotPassword}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="email" className={styles.formLabel}>דואר אלקטרוני</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="email"
                                            id="email"
                                            className={styles.formInput}
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={loading}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Mail className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    className={`${styles.loginButton} ${loading ? styles.disabled : ''}`}
                                    disabled={loading}
                                >
                                    {loading ? 'שולח...' : 'שלח קוד איפוס'}
                                </button>
                            </form>
                        )}

                        {step === 'code' && (
                            <form onSubmit={handleVerifyCode}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="code" className={styles.formLabel}>קוד אימות</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            id="code"
                                            className={styles.formInput}
                                            placeholder="הזן קוד 6 ספרות"
                                            value={resetCode}
                                            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            disabled={loading}
                                            maxLength="6"
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Mail className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    className={`${styles.loginButton} ${loading ? styles.disabled : ''}`}
                                    disabled={loading}
                                >
                                    {loading ? 'מאמת...' : 'אמת קוד'}
                                </button>

                                <div className={styles.verificationMessage} style={{ marginTop: '1rem' }}>
                                    <p>לא קיבלת קוד?</p>
                                    <button
                                        type="button"
                                        onClick={handleResendCode}
                                        className={`${styles.resendButton} ${styles.secondary}`}
                                        disabled={loading}
                                        style={{ marginTop: '0.5rem' }}
                                    >
                                        <RefreshCw 
                                            size={16} 
                                            className={loading ? styles.spinning : ''} 
                                        />
                                        {loading ? 'שולח...' : 'שלח קוד חדש'}
                                    </button>
                                    
                                    <button
                                        type="button"
                                        onClick={handleGoBack}
                                        className={`${styles.resendButton} ${styles.secondary}`}
                                        disabled={loading}
                                        style={{ marginTop: '0.5rem' }}
                                    >
                                        חזור לשלב הקודם
                                    </button>
                                </div>
                            </form>
                        )}

                        {step === 'password' && (
                            <form onSubmit={handleResetPassword}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="newPassword" className={styles.formLabel}>סיסמה חדשה</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="password"
                                            id="newPassword"
                                            className={styles.formInput}
                                            placeholder="הזן סיסמה חדשה"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            disabled={loading}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Lock className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="confirmPassword" className={styles.formLabel}>אימות סיסמה</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="password"
                                            id="confirmPassword"
                                            className={styles.formInput}
                                            placeholder="הזן את הסיסמה שוב"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            disabled={loading}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Lock className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    className={`${styles.loginButton} ${loading ? styles.disabled : ''}`}
                                    disabled={loading}
                                >
                                    {loading ? 'משנה סיסמה...' : 'שנה סיסמה'}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleGoBack}
                                    className={`${styles.resendButton} ${styles.secondary}`}
                                    disabled={loading}
                                    style={{ marginTop: '1rem' }}
                                >
                                    חזור לשלב הקודם
                                </button>
                            </form>
                        )}
                    </div>

                    {showVerificationHelp && (
                        <div className={styles.verificationMessage} style={{ 
                            marginTop: '1rem', 
                            padding: '1rem',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px'
                        }}>
                            <div className={styles.verificationPrimary} style={{
                                fontWeight: 'bold',
                                color: '#495057',
                                marginBottom: '0.5rem'
                            }}>
                                נדרש אימות כתובת מייל
                            </div>
                            <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                                כדי לאפס את הסיסמה, עליך קודם לאמת את כתובת המייל שלך.
                            </p>
                            <div className={styles.verificationActions} style={{
                                display: 'flex',
                                gap: '0.5rem',
                                flexDirection: 'column'
                            }}>
                                <button
                                    onClick={handleResendVerification}
                                    className={styles.resendButton}
                                    disabled={loading}
                                    style={{
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '4px',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <RefreshCw 
                                        size={16} 
                                        className={loading ? styles.spinning : ''} 
                                    />
                                    {loading ? 'שולח...' : 'שלח קוד אימות חדש'}
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    className={`${styles.resendButton} ${styles.secondary}`}
                                    disabled={loading}
                                    style={{
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '4px',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    מעבר לדף הרשמה
                                </button>
                            </div>
                            <p className={styles.verificationSecondary} style={{
                                fontSize: '0.875rem',
                                color: '#6c757d',
                                marginTop: '0.5rem',
                                textAlign: 'center'
                            }}>
                                כתובת המייל: {email}
                            </p>
                        </div>
                    )}

                    <div className={styles.registerContainer}>
                        <p className={styles.registerText}>
                            <button 
                                onClick={() => navigate('/login')}
                                className={styles.link}
                                style={{ 
                                    background: 'none', 
                                    border: 'none', 
                                    padding: 0,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <ArrowRight size={16} />
                                חזרה להתחברות
                            </button>
                        </p>
                    </div>
                </div>

                <div className={styles.footer}>
                    <p className={styles.copyright}>© 2025 ניהול ועד בית. כל הזכויות שמורות.</p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;